import React from 'react';
import { ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { useLanguage } from '../contexts/LanguageContext';

const HeroSection = () => {
  const { t } = useLanguage();
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="home"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background Image with Overlay */}
      <div className="absolute inset-0">
        <img
          src="/images/hero-bg.jpg"
          alt="Manufacturing Process"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-slate-900/95 via-slate-900/85 to-teal-900/90" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="max-w-4xl">
          {/* Company Name with Modern Typography */}
          <div className="mb-8">
            <h1 className="text-6xl sm:text-7xl lg:text-8xl font-bold text-white mb-4 leading-tight tracking-tight">
              {t.hero.companyName}
            </h1>
            <div className="h-1 w-32 bg-gradient-to-r from-teal-400 to-teal-600 mb-6"></div>
            <p className="text-xl sm:text-2xl text-gray-300 font-light max-w-2xl">
              {t.hero.companyFull}
            </p>
          </div>

          {/* Tagline with Glassmorphism */}
          <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-8 mb-10 shadow-2xl">
            <p className="text-2xl sm:text-3xl text-white font-semibold mb-4">
              {t.hero.tagline}
            </p>
            <p className="text-lg text-gray-200 leading-relaxed">
              {t.hero.description}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-start gap-4">
            <Button
              size="lg"
              onClick={() => scrollToSection('products')}
              className="bg-teal-600 hover:bg-teal-700 text-white px-8 py-6 text-lg font-semibold transition-all duration-300 group shadow-xl hover:shadow-2xl hover:scale-105"
            >
              {t.hero.exploreProducts}
              <ArrowRight className="ml-2 group-hover:translate-x-2 transition-transform" size={20} />
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={() => scrollToSection('contact')}
              className="border-2 border-white/50 text-white hover:bg-white/10 backdrop-blur-sm px-8 py-6 text-lg font-semibold transition-all duration-300 hover:scale-105"
            >
              {t.hero.contactUs}
            </Button>
          </div>

          {/* Stats Bar - Modern Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center hover:bg-white/15 transition-all">
              <div className="text-3xl font-bold text-teal-400 mb-1">7000+</div>
              <div className="text-sm text-gray-300">Ton/Yıl Kapasite</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center hover:bg-white/15 transition-all">
              <div className="text-3xl font-bold text-teal-400 mb-1">%100</div>
              <div className="text-sm text-gray-300">Memnuniyet</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center hover:bg-white/15 transition-all">
              <div className="text-3xl font-bold text-teal-400 mb-1">25+</div>
              <div className="text-sm text-gray-300">Yıl Deneyim</div>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-xl p-4 text-center hover:bg-white/15 transition-all">
              <div className="text-3xl font-bold text-teal-400 mb-1">15+</div>
              <div className="text-sm text-gray-300">İhracat Ülkesi</div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator - Modern */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2">
        <div className="flex flex-col items-center animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2 mb-2">
            <div className="w-1 h-3 bg-white/50 rounded-full animate-pulse" />
          </div>
          <span className="text-white/70 text-xs font-medium">Scroll</span>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
